import React, { useState, useEffect, useCallback } from "react";
import { Flex, Spinner } from "@chakra-ui/react";
import Modal from "newui/components/ui/Modal/Modal";
import { Button } from "newui/components/Button";
import { CompactTextInput } from "newui/components/ui/InputWithLabel/CompactTextInput";
import { CheckinRow } from "./CheckinRow";
import type { CheckinAttendee, CheckinConfig, AddAttendeeForm } from "./types";

interface LocalAttendee extends CheckinAttendee {
  localComment: string;
}

interface AttendeeCheckinModalProps {
  /** Attendees to display */
  attendees: CheckinAttendee[];
  /** Configuration for labels, title, and add-form flags */
  config: CheckinConfig;
  /** Whether the add-attendee action is in progress */
  isAdding?: boolean;
  /** Whether the continue action is in progress */
  isContinuing?: boolean;
  /** Whether attendees are still loading */
  isLoading?: boolean;
  /** Modal open state */
  isOpen: boolean;
  /** Called when the modal is closed */
  onClose: () => void;
  /** Called when a comment is finalised (blur) */
  onCommentSave: (
    attendeeId: number,
    status: "attending" | "not_attending",
    comment: string
  ) => void;
  /** Called when the continue/confirm button is clicked */
  onContinue: () => void;
  /** Called when an attendee's status changes */
  onStatusChange: (
    attendeeId: number,
    status: "attending" | "not_attending",
    comment?: string
  ) => void;
  /** Called when a late/ad-hoc attendee is added */
  onAddAttendee?: (form: AddAttendeeForm) => void;
}

export const AttendeeCheckinModal: React.FC<AttendeeCheckinModalProps> = ({
  isOpen,
  attendees,
  isLoading = false,
  config,
  isContinuing = false,
  isAdding = false,
  onClose,
  onContinue,
  onStatusChange,
  onCommentSave,
  onAddAttendee,
}) => {
  const [localAttendees, setLocalAttendees] = useState<LocalAttendee[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addName, setAddName] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [addFlags, setAddFlags] = useState<Record<string, boolean>>(() => {
    const defaults: Record<string, boolean> = {};
    config.addFlags?.forEach((f) => {
      defaults[f.key] = f.default ?? false;
    });
    return defaults;
  });
  const [addError, setAddError] = useState("");

  useEffect(() => {
    if (attendees.length > 0) {
      setLocalAttendees(
        attendees.map((a) => ({ ...a, localComment: a.comment ?? "" }))
      );
    }
  }, [attendees]);

  const handleStatusChange = useCallback(
    (attendeeId: number, status: "attending" | "not_attending") => {
      setLocalAttendees((prev) =>
        prev.map((a) =>
          a.id !== attendeeId ? a : { ...a, attendance_status: status }
        )
      );
      const attendee = localAttendees.find((a) => a.id === attendeeId);
      onStatusChange(attendeeId, status, attendee?.localComment || undefined);
    },
    [localAttendees, onStatusChange]
  );

  const handleCommentChange = useCallback(
    (attendeeId: number, comment: string) => {
      setLocalAttendees((prev) =>
        prev.map((a) =>
          a.id !== attendeeId ? a : { ...a, localComment: comment }
        )
      );
    },
    []
  );

  const handleCommentBlur = useCallback(
    (attendeeId: number) => {
      const attendee = localAttendees.find((a) => a.id === attendeeId);
      if (!attendee || attendee.attendance_status === "pending") return;
      onCommentSave(
        attendeeId,
        attendee.attendance_status as "attending" | "not_attending",
        attendee.localComment
      );
    },
    [localAttendees, onCommentSave]
  );

  const handleAdd = () => {
    if (!addName.trim() || !addEmail.trim()) {
      setAddError("Name and email are required.");
      return;
    }
    setAddError("");
    onAddAttendee?.({
      name: addName.trim(),
      email: addEmail.trim(),
      flags: addFlags,
    });
    setAddName("");
    setAddEmail("");
    setShowAddForm(false);
    const defaults: Record<string, boolean> = {};
    config.addFlags?.forEach((f) => {
      defaults[f.key] = f.default ?? false;
    });
    setAddFlags(defaults);
  };

  const resetAddForm = () => {
    setShowAddForm(false);
    setAddError("");
    setAddName("");
    setAddEmail("");
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={config.title}
      iconType={config.iconType ?? "info"}
      size="lg"
      closeOnOverlayClick={!isContinuing}
      customFooter={
        <div className="flex w-full justify-end gap-2">
          <Button
            btntype="secondary"
            type="button"
            onClick={onClose}
            disabled={isContinuing}
          >
            Cancel
          </Button>
          <Button
            btntype="primary"
            type="button"
            onClick={onContinue}
            disabled={isContinuing}
            isLoading={isContinuing}
            data-testid="checkin-continue-btn"
          >
            {config.continueLabel}
          </Button>
        </div>
      }
    >
      {isLoading ? (
        <Flex justify="center" py={6}>
          <Spinner />
        </Flex>
      ) : (
        <div className="space-y-4">
          <div className="max-h-[400px] overflow-y-auto pr-3">
            {localAttendees.map((attendee) => (
              <CheckinRow
                key={attendee.id}
                attendee={attendee}
                commentValue={attendee.localComment}
                onStatusChange={handleStatusChange}
                onCommentChange={handleCommentChange}
                onCommentBlur={handleCommentBlur}
                isDisabled={isContinuing}
              />
            ))}
            {localAttendees.length === 0 && (
              <p className="text-sm text-gray-500 py-4 text-center">
                No attendees found.
              </p>
            )}
          </div>

          {onAddAttendee && (
            <>
              <hr className="border-gray-200" />

              {showAddForm ? (
                <div>
                  <p className="text-sm font-medium mb-3">
                    {config.addLabel ?? "Add Attendee"}
                  </p>
                  <div className="flex flex-col gap-2">
                    <CompactTextInput
                      placeholder="Full name"
                      value={addName}
                      onChange={(e) => setAddName(e.target.value)}
                      disabled={isAdding}
                      data-testid="checkin-add-name"
                    />
                    <CompactTextInput
                      type="email"
                      placeholder="Email address"
                      value={addEmail}
                      onChange={(e) => setAddEmail(e.target.value)}
                      disabled={isAdding}
                      data-testid="checkin-add-email"
                    />
                    {config.addFlags?.map((flag) => (
                      <label key={flag.key} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={addFlags[flag.key] ?? false}
                          onChange={(e) =>
                            setAddFlags((prev) => ({
                              ...prev,
                              [flag.key]: e.target.checked,
                            }))
                          }
                          disabled={isAdding}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm">{flag.label}</span>
                      </label>
                    ))}
                    {addError && (
                      <p className="text-sm text-red-500">{addError}</p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        btntype="primary"
                        type="button"
                        onClick={handleAdd}
                        disabled={isAdding}
                        isLoading={isAdding}
                        data-testid="checkin-confirm-add"
                      >
                        Add Attendee
                      </Button>
                      <Button
                        btntype="secondary"
                        type="button"
                        onClick={resetAddForm}
                        disabled={isAdding}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <Button
                  btntype="plain"
                  type="button"
                  onClick={() => setShowAddForm(true)}
                  disabled={isContinuing}
                  data-testid="checkin-add-btn"
                >
                  + {config.addLabel ?? "Add Attendee"}
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </Modal>
  );
};
